import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import KopSurat from "@/components/KopSurat";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Send, Plus, Printer, FileText, Search, Edit3, Trash2, Copy, Sparkles, Settings2, Eye } from "lucide-react";

const CATEGORIES = [
  { key: "UMUM", label: "Umum" },
  { key: "UNDANGAN", label: "Undangan" },
  { key: "KEPUTUSAN", label: "Keputusan" },
  { key: "PEMBERITAHUAN", label: "Pemberitahuan" },
  { key: "LAPORAN", label: "Laporan" },
];
const STATUS_MAP = {
  DRAFT: { tone: "muted", label: "Draft" },
  DITERBITKAN: { tone: "info", label: "Diterbitkan" },
  DIKIRIM: { tone: "success", label: "Dikirim" },
  ARSIP: { tone: "muted", label: "Arsip" },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "—";

const EMPTY_FORM = {
  letter_number: "",
  subject: "",
  recipient: "",
  recipient_address: "",
  date_letter: todayISO(),
  salutation: "Salam Sejahtera dalam Kasih Kristus,",
  body: "",
  closing: "Hormat kami,",
  signer_name: "",
  signer_title: "Ketua Klasis Mimika",
  cosigner_name: "",
  cosigner_title: "Sekretaris Klasis Mimika",
  attachments: [],
  tembusan: [],
  category: "UMUM",
  status: "DRAFT",
};

export default function SuratKeluar() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({});
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [openCompose, setOpenCompose] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const printRef = useRef();

  const canManage = ["SUPER_ADMIN", "ADMIN_KLASIS", "SEKRETARIS_KLASIS", "WAKIL_SEKRETARIS", "KETUA_KLASIS"].includes(user?.role);
  const canEditKop = ["SUPER_ADMIN", "ADMIN_KLASIS", "SEKRETARIS_KLASIS"].includes(user?.role);

  const loadList = () => api.get("/letters/outgoing").then(r => setItems(r.data)).finally(() => setLoading(false));
  const loadProfile = () => api.get("/klasis-profile").then(r => setProfile(r.data || {}));

  useEffect(() => { loadList(); loadProfile(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      signer_name: profile?.ketua_klasis || "",
      cosigner_name: profile?.sekretaris_klasis || "",
    });
    setOpenCompose(true);
  };

  const openEdit = async (item) => {
    setEditingId(item.id);
    setForm({
      letter_number: item.letter_number || "",
      subject: item.subject || "",
      recipient: item.recipient || "",
      recipient_address: item.recipient_address || "",
      date_letter: item.date_letter || todayISO(),
      salutation: item.salutation || "Salam Sejahtera dalam Kasih Kristus,",
      body: item.body || "",
      closing: item.closing || "Hormat kami,",
      signer_name: item.signer_name || "",
      signer_title: item.signer_title || "Ketua Klasis Mimika",
      cosigner_name: item.cosigner_name || "",
      cosigner_title: item.cosigner_title || "Sekretaris Klasis Mimika",
      attachments: item.attachments || [],
      tembusan: item.tembusan || [],
      category: item.category || "UMUM",
      status: item.status || "DRAFT",
    });
    setOpenCompose(true);
  };

  const submit = async () => {
    if (!form.subject.trim() || !form.recipient.trim() || !form.body.trim() || !form.date_letter) {
      toast.error("Perihal, penerima, tanggal, dan isi surat wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Trim empty arrays: nothing to do
      if (editingId) {
        await api.patch(`/letters/outgoing/${editingId}`, payload);
        toast.success("Surat diperbarui");
      } else {
        await api.post("/letters/outgoing", payload);
        toast.success("Surat tersimpan sebagai " + (payload.status === "DRAFT" ? "draft" : "diterbitkan"));
      }
      setOpenCompose(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`Hapus surat "${it.subject}"?`)) return;
    try { await api.delete(`/letters/outgoing/${it.id}`); toast.success("Terhapus"); loadList(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicate = (it) => {
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      subject: `[Salinan] ${it.subject}`,
      recipient: it.recipient,
      recipient_address: it.recipient_address,
      salutation: it.salutation,
      body: it.body,
      closing: it.closing,
      signer_name: it.signer_name,
      signer_title: it.signer_title,
      cosigner_name: it.cosigner_name,
      cosigner_title: it.cosigner_title,
      category: it.category,
      tembusan: it.tembusan,
      attachments: it.attachments,
      date_letter: todayISO(),
    });
    setOpenCompose(true);
    toast.info("Salinan surat siap diedit");
  };

  const openPreview = (it) => setPreview(it);
  const printLetter = () => {
    if (typeof window !== "undefined") window.print();
  };

  // Kop editor
  const openKopEditor = () => {
    setProfileForm({
      address: profile.address || "",
      address2: profile.address2 || "",
      phone: profile.phone || "",
      email: profile.email || "",
      website: profile.website || "",
    });
    setProfileOpen(true);
  };
  const saveKop = async () => {
    try {
      await api.patch("/klasis-profile", profileForm);
      toast.success("Kop surat diperbarui");
      setProfileOpen(false);
      loadProfile();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter(i =>
      !q ||
      (i.letter_number || "").toLowerCase().includes(q) ||
      (i.subject || "").toLowerCase().includes(q) ||
      (i.recipient || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Administrasi</div>
          <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
            <Send className="h-7 w-7 text-secondary" /> Surat Keluar
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Konsep surat resmi GPDP Klasis Mimika dengan kop otomatis, penomoran, dan cetak PDF siap tanda tangan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEditKop && (
            <Button variant="outline" size="sm" className="rounded-full" onClick={openKopEditor} data-testid="btn-edit-kop">
              <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Kop Surat
            </Button>
          )}
          {canManage && (
            <Button className="rounded-full" onClick={openCreate} data-testid="btn-new-outgoing">
              <Plus className="h-4 w-4 mr-2" /> Konsep Baru
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Cari nomor, perihal, atau penerima…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9" data-testid="input-search-outgoing" />
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Memuat…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="outgoing-empty">
          <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada surat keluar.</div>
          {canManage && <div className="text-xs text-muted-foreground mt-1">Klik <strong>Konsep Baru</strong> untuk mulai.</div>}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Nomor / Tanggal</th>
                  <th className="text-left p-3">Perihal</th>
                  <th className="text-left p-3">Penerima</th>
                  <th className="text-left p-3">Kategori</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const st = STATUS_MAP[it.status] || STATUS_MAP.DRAFT;
                  return (
                    <tr key={it.id} className="border-t border-border hover:bg-muted/20" data-testid={`row-outgoing-${it.id}`}>
                      <td className="p-3 align-top">
                        <div className="font-mono text-xs text-primary">{it.letter_number || "—"}</div>
                        <div className="text-[0.7rem] text-muted-foreground mt-0.5">{fmtDate(it.date_letter)}</div>
                      </td>
                      <td className="p-3 align-top">
                        <div className="font-medium">{it.subject}</div>
                      </td>
                      <td className="p-3 align-top text-xs">{it.recipient}</td>
                      <td className="p-3 align-top text-xs">{CATEGORIES.find(c => c.key === it.category)?.label || it.category}</td>
                      <td className="p-3 align-top"><StatusBadge status={st.tone} label={st.label} /></td>
                      <td className="p-3 align-top text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openPreview(it)} className="p-1.5 text-muted-foreground hover:text-primary" title="Preview" data-testid={`btn-preview-${it.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          {canManage && (
                            <>
                              <button onClick={() => openEdit(it)} className="p-1.5 text-muted-foreground hover:text-primary" title="Edit" data-testid={`btn-edit-${it.id}`}>
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => duplicate(it)} className="p-1.5 text-muted-foreground hover:text-primary" title="Duplikat" data-testid={`btn-dup-${it.id}`}>
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => del(it)} className="p-1.5 text-muted-foreground hover:text-destructive" title="Hapus" data-testid={`btn-del-${it.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Compose Dialog */}
      <Dialog open={openCompose} onOpenChange={(o) => { if (!o) { setForm(EMPTY_FORM); setEditingId(null); } setOpenCompose(o); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Surat Keluar" : "Konsep Surat Baru"}</DialogTitle>
            <DialogDescription>Nomor akan otomatis di-generate saat diterbitkan. Kop akan menempel di dokumen cetak.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                  <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tanggal Surat</Label>
                <Input type="date" value={form.date_letter} onChange={(e)=>setForm({...form, date_letter:e.target.value})} data-testid="input-date-letter" />
              </div>
              <div>
                <Label>Nomor Surat</Label>
                <Input value={form.letter_number} onChange={(e)=>setForm({...form, letter_number:e.target.value})} placeholder="Kosongkan → auto" data-testid="input-letter-number" />
              </div>
            </div>
            <div>
              <Label>Perihal</Label>
              <Input value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} placeholder="Contoh: Undangan RAKER 2027" data-testid="input-subject" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kepada (Yth.)</Label>
                <Input value={form.recipient} onChange={(e)=>setForm({...form, recipient:e.target.value})} placeholder="Contoh: Bapak Bupati Mimika" data-testid="input-recipient" />
              </div>
              <div>
                <Label>Alamat Penerima</Label>
                <Input value={form.recipient_address} onChange={(e)=>setForm({...form, recipient_address:e.target.value})} placeholder="Di Timika" data-testid="input-recipient-address" />
              </div>
            </div>
            <div>
              <Label>Salam Pembuka</Label>
              <Input value={form.salutation} onChange={(e)=>setForm({...form, salutation:e.target.value})} data-testid="input-salutation" />
            </div>
            <div>
              <Label>Isi Surat</Label>
              <Textarea rows={8} value={form.body} onChange={(e)=>setForm({...form, body:e.target.value})} placeholder="Tulis isi surat di sini. Gunakan enter untuk paragraf baru…" data-testid="input-body" />
              <div className="text-[0.65rem] text-muted-foreground mt-1"><Sparkles className="h-3 w-3 inline mr-0.5" /> Tip: Gunakan enter untuk paragraf baru. Sistem otomatis menjaga rata kiri/kanan saat cetak.</div>
            </div>
            <div>
              <Label>Salam Penutup</Label>
              <Input value={form.closing} onChange={(e)=>setForm({...form, closing:e.target.value})} data-testid="input-closing" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nama Penandatangan</Label>
                <Input value={form.signer_name} onChange={(e)=>setForm({...form, signer_name:e.target.value})} data-testid="input-signer-name" />
              </div>
              <div>
                <Label>Jabatan Penandatangan</Label>
                <Input value={form.signer_title} onChange={(e)=>setForm({...form, signer_title:e.target.value})} data-testid="input-signer-title" />
              </div>
              <div>
                <Label>Nama Pendamping (opsional)</Label>
                <Input value={form.cosigner_name} onChange={(e)=>setForm({...form, cosigner_name:e.target.value})} data-testid="input-cosigner-name" />
              </div>
              <div>
                <Label>Jabatan Pendamping</Label>
                <Input value={form.cosigner_title} onChange={(e)=>setForm({...form, cosigner_title:e.target.value})} data-testid="input-cosigner-title" />
              </div>
            </div>
            <div>
              <Label>Tembusan (pisahkan dengan koma)</Label>
              <Input
                value={(form.tembusan || []).join(", ")}
                onChange={(e)=>setForm({...form, tembusan: e.target.value.split(",").map(x=>x.trim()).filter(Boolean)})}
                placeholder="Ketua Sinode, Arsip"
                data-testid="input-tembusan"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="DITERBITKAN">Diterbitkan</SelectItem>
                  <SelectItem value="DIKIRIM">Dikirim</SelectItem>
                  <SelectItem value="ARSIP">Arsip</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenCompose(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-save-outgoing">{saving ? "Menyimpan…" : (editingId ? "Simpan Perubahan" : "Simpan Konsep")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview / Print */}
      <Dialog open={!!preview} onOpenChange={(o)=>!o && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 gap-0">
          <div className="flex items-center justify-between p-3 border-b border-border print:hidden">
            <div className="text-xs text-muted-foreground">Preview Surat {preview?.letter_number || "(tanpa nomor)"}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={printLetter} data-testid="btn-print-letter">
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Cetak / PDF
              </Button>
            </div>
          </div>
          <div className="surat-print-root px-10 py-8 bg-white" ref={printRef}>
            <KopSurat profile={profile} />
            {preview && (
              <div className="mt-6 font-serif text-[13px] leading-relaxed text-neutral-900" data-testid="surat-body">
                <div className="flex justify-between items-start text-sm">
                  <div className="space-y-0.5">
                    <div>Nomor : <span className="font-mono">{preview.letter_number || "—"}</span></div>
                    {preview.attachments?.length ? <div>Lampiran : {preview.attachments.length} berkas</div> : <div>Lampiran : —</div>}
                    <div>Perihal : <strong className="uppercase">{preview.subject}</strong></div>
                  </div>
                  <div className="text-right">
                    <div>Timika, {fmtDate(preview.date_letter)}</div>
                  </div>
                </div>
                <div className="mt-6">
                  <div>Kepada Yth.</div>
                  <div className="font-semibold">{preview.recipient}</div>
                  {preview.recipient_address && <div>{preview.recipient_address}</div>}
                </div>
                <div className="mt-6 italic">{preview.salutation}</div>
                <div className="mt-4 space-y-3 text-justify">
                  {(preview.body || "").split(/\n\n+|\n/).filter(Boolean).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <div className="mt-8 grid grid-cols-2">
                  <div />
                  <div>
                    <div className="italic mb-14">{preview.closing}</div>
                    <div className="font-bold underline">{preview.signer_name || "________________________"}</div>
                    <div>{preview.signer_title}</div>
                  </div>
                </div>
                {preview.cosigner_name && (
                  <div className="mt-4 grid grid-cols-2">
                    <div className="pr-4">
                      <div className="italic mb-14">Mengetahui,</div>
                      <div className="font-bold underline">{preview.cosigner_name}</div>
                      <div>{preview.cosigner_title}</div>
                    </div>
                    <div />
                  </div>
                )}
                {preview.tembusan?.length > 0 && (
                  <div className="mt-8 text-xs">
                    <div className="font-bold underline">Tembusan:</div>
                    <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                      {preview.tembusan.map((t, i) => <li key={i}>{t}</li>)}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Kop editor dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pengaturan Kop Surat</DialogTitle>
            <DialogDescription>Data alamat & kontak yang tampil di kop semua surat keluar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Alamat Baris 1</Label>
              <Input value={profileForm.address || ""} onChange={(e)=>setProfileForm({...profileForm, address:e.target.value})} placeholder="Jl. Cendrawasih Timika Indah, Kabupaten Mimika" data-testid="input-kop-address" />
            </div>
            <div>
              <Label>Alamat Baris 2</Label>
              <Input value={profileForm.address2 || ""} onChange={(e)=>setProfileForm({...profileForm, address2:e.target.value})} placeholder="Papua Tengah 99910" data-testid="input-kop-address2" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telepon</Label>
                <Input value={profileForm.phone || ""} onChange={(e)=>setProfileForm({...profileForm, phone:e.target.value})} data-testid="input-kop-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={profileForm.email || ""} onChange={(e)=>setProfileForm({...profileForm, email:e.target.value})} data-testid="input-kop-email" />
              </div>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={profileForm.website || ""} onChange={(e)=>setProfileForm({...profileForm, website:e.target.value})} data-testid="input-kop-website" />
            </div>
            {/* Kop preview */}
            <div className="border border-border rounded-md p-3 bg-white mt-2">
              <KopSurat profile={{ ...profile, ...profileForm }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setProfileOpen(false)}>Batal</Button>
            <Button onClick={saveKop} data-testid="btn-save-kop">Simpan Kop</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
